
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DateMultiPicker } from "@/components/ui/date-multi-picker";
import { meApi } from "@/lib/api/me";
import { getMeSession } from "@/lib/auth/storage";
import { formatNoticeDateList, todayIST } from "@/lib/timezone";
import { useMeNotices } from "@/hooks/use-me-queries";
import {
  REQUESTED_STATUS_OPTIONS,
  noticeDates,
  noticeRequestedStatus,
  type NoticeApprovalStatus,
  type NoticeSession,
  type RequestedStatus,
} from "@/lib/types";

const SESSIONS: { value: NoticeSession; label: string }[] = [
  { value: "whole_day", label: "Whole day" },
  { value: "morning", label: "Morning" },
  { value: "morning_block", label: "Morning block (9–1)" },
  { value: "evening_block", label: "Evening block (5–9)" },
];

function approvalLabel(
  status: NoticeApprovalStatus,
  requested: RequestedStatus
): string {
  if (status === "submitted") return "Pending review";
  if (status === "approved") return `Approved — marked ${requested}`;
  return "Rejected — marked A";
}

const APPROVAL_VARIANT: Record<
  NoticeApprovalStatus,
  "default" | "secondary" | "outline"
> = {
  submitted: "default",
  approved: "secondary",
  rejected: "outline",
};

export default function MeNoticePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meSession = getMeSession();
  const { data: notices, isLoading: noticesLoading } = useMeNotices();
  const [dates, setDates] = useState<string[]>([todayIST()]);
  const [session, setSession] = useState<NoticeSession>("whole_day");
  const [requestedStatus, setRequestedStatus] = useState<RequestedStatus>("O");
  const [notes, setNotes] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      meApi.submitNotice(meSession!, {
        dates,
        session,
        requested_status: requestedStatus,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me", "notices"] });
      setNotes("");
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Notices</h2>
        <p className="text-sm text-muted-foreground">
          Select one or more dates and request O, S, or TP. Your coordinator
          will approve (marks those dates) or reject (marks A).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your notices</CardTitle>
          <CardDescription>Status updates when admin reviews</CardDescription>
        </CardHeader>
        <CardContent>
          {noticesLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {!noticesLoading && (notices?.length ?? 0) === 0 && (
            <p className="text-sm text-muted-foreground">
              No notices submitted yet.
            </p>
          )}
          <ul className="space-y-3">
            {notices?.map((notice) => {
              const requested = noticeRequestedStatus(notice);
              const noticeDateList = noticeDates(notice);
              return (
                <li
                  key={notice.id}
                  className="rounded-lg border border-saffron-100 p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium">
                      {formatNoticeDateList(noticeDateList)}
                      {noticeDateList.length > 1 && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({noticeDateList.length} days)
                        </span>
                      )}
                    </p>
                    <Badge variant={APPROVAL_VARIANT[notice.approval_status]}>
                      {approvalLabel(notice.approval_status, requested)}
                    </Badge>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    {requested} · {notice.session.replace(/_/g, " ")}
                    {notice.reason ? ` · ${notice.reason}` : ""}
                  </p>
                  {notice.reviewed_at &&
                    notice.approval_status !== "submitted" && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Reviewed {notice.reviewed_at.slice(0, 10)}
                      </p>
                    )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Submit new notice</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (dates.length === 0) return;
              submit.mutate();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label>Dates</Label>
              <DateMultiPicker value={dates} onChange={setDates} />
            </div>

            <div className="space-y-2">
              <Label>Mark as</Label>
              <Select
                value={requestedStatus}
                onValueChange={(v) =>
                  setRequestedStatus(v as RequestedStatus)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUESTED_STATUS_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Session</Label>
              <Select
                value={session}
                onValueChange={(v) => setSession(v as NoticeSession)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SESSIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any extra details…"
              />
            </div>

            {submit.isError && (
              <p className="text-sm text-red-600">Could not submit notice.</p>
            )}
            {submit.isSuccess && (
              <p className="text-sm text-emerald-700">
                Notice submitted — awaiting coordinator review.
              </p>
            )}

            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1"
                size="lg"
                disabled={submit.isPending || dates.length === 0}
              >
                {submit.isPending ? "Submitting…" : "Submit notice"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate("/me")}
              >
                Done
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
